import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface EditableTargetDateProps {
  date: Date | null;
  onSave: (date: Date | null) => void;
}

/**
 * Editable target completion date with inline date picker
 */
export function EditableTargetDate({ date, onSave }: EditableTargetDateProps) {
  const { t } = useTranslation(['dashboard', 'common']);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(date || undefined);

  const handleSave = () => {
    onSave(selectedDate || null);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setSelectedDate(date || undefined);
    setIsEditing(false);
  };

  const formatDate = (dateToFormat: Date | null): string => {
    if (!dateToFormat) return t('common:date.notSet');
    return format(dateToFormat, 'MMM d, yyyy');
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <div className="text-2xl font-bold">{formatDate(date)}</div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsEditing(true)}
          className="h-8 w-8 p-0"
          aria-label={t('common:actions.edit')}
        >
          <Pencil className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              'justify-start text-left font-normal',
              !selectedDate && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" aria-hidden="true" />
            {selectedDate ? format(selectedDate, 'PPP') : <span>{t('dashboard:targetDate.pickDate')}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleSave}
        className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
        aria-label={t('common:actions.save')}
      >
        <Check className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleCancel}
        className="h-8 w-8 p-0 text-destructive hover:text-destructive/90"
        aria-label={t('common:actions.cancel')}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
